import { AnalyticsBrowser } from '@segment/analytics-next';
import { CONFIG } from '../../config';
import { initializeInsights } from '../algolia/initializeInsights';

const SEGMENT_CDN_HOST = 'https://cdn.segment.com';
const SEGMENT_API_HOST = 'api.segment.io/v1';
const SEGMENT_METHODS = [
  'trackSubmit',
  'trackClick',
  'trackLink',
  'trackForm',
  'pageview',
  'identify',
  'reset',
  'group',
  'track',
  'ready',
  'alias',
  'debug',
  'page',
  'once',
  'off',
  'on',
  'addSourceMiddleware',
  'addIntegrationMiddleware',
  'setAnonymousId',
  'addDestinationMiddleware',
  'register'
] as const;

type SegmentOptions = {
  cdnHost: string;
  anonymousId?: string;
  integrations: {
    All: boolean;
    'Segment.io': { apiHost: string };
    'Google Analytics'?: boolean;
  };
};

type SegmentAnalyticsQueue = Omit<AnalyticsBrowser, 'load'> &
  unknown[] & {
    _loadOptions?: SegmentOptions;
    initialize?: unknown;
    invoked?: boolean;
    loaded?: boolean;
    methods?: readonly string[];
    factory?: (method: string) => (...args: unknown[]) => SegmentAnalyticsQueue;
    load?: (key: string, options: SegmentOptions) => void;
  };

/**
 * Creates a Segment analytics.js-compatible queue stub on `window.analytics`.
 *
 * This mirrors the official Segment snippet's buffering behavior: method calls
 * (track, identify, page, etc.) are pushed onto an array queue and replayed
 * once the real analytics.js SDK loads. This lets us capture events immediately
 * — before consent is determined — without losing them.
 *
 * See: https://www.twilio.com/docs/segment/connections/sources/catalog/libraries/website/javascript
 */
function initializeSegmentQueue() {
  const analytics = (window.analytics || []) as unknown as SegmentAnalyticsQueue;
  window.analytics = analytics as unknown as AnalyticsBrowser;

  if (analytics.initialize || analytics.invoked) {
    return analytics;
  }

  analytics.invoked = true;
  analytics.methods = SEGMENT_METHODS;
  analytics.factory = function factory(method: string) {
    return function queueMethod(...args: unknown[]) {
      analytics.push([method, ...args]);
      return analytics;
    };
  };

  SEGMENT_METHODS.forEach(method => {
    (analytics as unknown as Record<string, unknown>)[method] = analytics.factory?.(method);
  });

  analytics.load = (key: string, options: SegmentOptions) => {
    if (analytics.loaded) return;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `${options.cdnHost}/analytics.js/v1/${key}/analytics.min.js`;

    const firstScript = document.getElementsByTagName('script')[0];
    if (!firstScript?.parentNode) return;

    firstScript.parentNode.insertBefore(script, firstScript);
    analytics._loadOptions = options;
    analytics.loaded = true;
  };

  return analytics;
}

function loadSegmentAfterConsent(options: SegmentOptions) {
  const analytics = initializeSegmentQueue();

  if (analytics.initialize || analytics.loaded) {
    return;
  }

  analytics.load?.(CONFIG.SEGMENT_WRITE_KEY, options);
  if (options.anonymousId) {
    analytics.setAnonymousId(options.anonymousId);
  }
}

/** Install the queue stub immediately so early track/identify calls are buffered. */
export function installSegmentQueue() {
  initializeSegmentQueue();
}

/** Load the real Segment SDK with all integrations enabled. */
export function loadSegmentWithTags(anonymousId: string | null) {
  loadSegmentAfterConsent({
    cdnHost: SEGMENT_CDN_HOST,
    anonymousId: anonymousId ?? undefined,
    integrations: {
      All: true,
      'Segment.io': { apiHost: SEGMENT_API_HOST }
    }
  });

  // Only initialize Algolia Insights if user has given consent
  initializeInsights();
}

/** Load the real Segment SDK with tags disabled (no consent). */
export function loadSegmentWithoutTags(anonymousId: string | null) {
  loadSegmentAfterConsent({
    cdnHost: SEGMENT_CDN_HOST,
    anonymousId: anonymousId ?? undefined,
    integrations: {
      All: false,
      'Segment.io': { apiHost: SEGMENT_API_HOST },
      'Google Analytics': true
    }
  });
}
